import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IconPencil } from "@/components/icons";

import { ROUTES } from "@/config/routes.ts";
import { useJwtCreate } from "@/hooks/api/login.hooks.ts";
import { cn } from "@/utils/cn.ts";

import Spinner from "../helpers/Spinner/Spinner";

interface KidsProfileCardProps {
  id: number;
  name: string;
  age: number;
  img: string;
  hero_color: string;
  hero_img: string;
  profile_color: string;
  profile_img?: string;
  reverse?: boolean;
}

const KidsProfileCard = ({
  id,
  age,
  hero_color,
  hero_img,
  img,
  name,
  profile_color,
  profile_img,
  reverse,
}: KidsProfileCardProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isloadingEdit, setIsLoadingEdit] = useState(false);

  const { mutate } = useJwtCreate({ profileId: id.toString(), age: age.toString() });

  const handleClick = () => {
    mutate(
      { profile_id: id.toString() },
      {
        onSuccess: () => navigate(ROUTES.MAIN.MAIN),
      },
    );
  };

  const handleClickEdit = () => {
    setIsLoadingEdit(true);

    mutate(
      { profile_id: id.toString() },
      {
        onSuccess: () => {
          navigate(ROUTES.GATE.UPDATE_PROFILE.replace(":id", id.toString()));
        },
        onError: () => {
          console.log("error");
        },
        onSettled: () => {
          setIsLoadingEdit(false);
        },
      },
    );
  };

  return (
    <>
      <div
        className={cn(
          "w-full max-w-[21rem] rounded-5 bg-white",
          "outline outline-2 outline-transparent transition-outline hover:outline-orange-1",
          "max-sm:max-w-none",
        )}
      >
        <button
          type={"button"}
          aria-label={name}
          onClick={handleClick}
          className={"relative h-40 w-full rounded-5 max-lg:h-[8.75rem]"}
          style={{ background: hero_color }}
        >
          <img
            src={hero_img}
            alt={"profile-image"}
            className={cn("absolute bottom-0 h-44 object-contain object-bottom max-lg:h-[9.5rem]", {
              "right-0": reverse,
              "-left-4": !reverse,
            })}
          />
          <img
            src={img}
            alt={"profile-image"}
            className={cn(
              "absolute right-6 top-1/2 h-32 w-40 -translate-y-1/2 transform object-contain object-center",
              {
                "left-0": reverse,
              },
            )}
          />
        </button>
        <div className={"text-on-surface flex items-center justify-between px-3.5 py-4 max-lg:px-4 max-lg:py-2"}>
          <div className={"flex items-center gap-2"}>
            <div className={"relative rounded-full"} style={{ background: profile_color }}>
              <img src={profile_img} alt={"profile-image"} className={"size-8 rounded-full object-cover object-top"} />
              <button
                onClick={handleClickEdit}
                title={t("Actions.edit")}
                className={
                  "absolute left-0 top-0 flex size-full items-center justify-center rounded-full bg-black bg-opacity-40"
                }
              >
                {!isloadingEdit ? (
                  <IconPencil className={"fill-on-primary size-3"} />
                ) : (
                  <Spinner className={"fill-on-primary size-4"} />
                )}
              </button>
            </div>
            <p>{name}</p>
          </div>
          <p>{t("General.above", { value: age })}</p>
        </div>
      </div>
    </>
  );
};

export default KidsProfileCard;
